import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAppSettingsByCategory } from '@/services/appSettingsService';
import { debugLogger } from '@/utils/debug';
import { ALL_FEATURE_FLAG_KEYS, FEATURE_FLAG_DEFAULTS, FeatureFlagKey, buildFeatureFlags } from '@/config/featureFlags';

export type FeatureFlagsRecord = Record<FeatureFlagKey, boolean>;

interface FeatureFlagsContextValue {
  flags: FeatureFlagsRecord;
  loading: boolean;
  getFlag: (key: FeatureFlagKey) => boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(undefined);

export const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data = [], isLoading } = useQuery({
    queryKey: ['featureFlags'],
    queryFn: async () => {
      debugLogger.log('Loading all feature flags');
      try {
        return await fetchAppSettingsByCategory('Feature Flags');
      } catch (error) {
        debugLogger.error('Error loading feature flags', error);
        return [] as { key: string; value: string }[];
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const flags: FeatureFlagsRecord = useMemo(() => {
    const raw: Record<string, string> = {};
    (data || []).forEach(item => { raw[item.key] = item.value; });
    return buildFeatureFlags(raw);
  }, [data]);

  const value: FeatureFlagsContextValue = {
    flags,
    loading: isLoading,
  getFlag: (key) => flags[key]
  };

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export function useFeatureFlagsContext() {
  return useContext(FeatureFlagsContext);
}
